import { CreateInstitutionForm } from './types'

/**
 * Validation rules for the Create Institution form.
 *
 * Keep these in sync with the backend:
 *  - Alias           -> InstitutionDomain.IsValidAlias (Domain.cs)
 *  - BankOne code    -> 3-7 digits
 *  - Admin email     -> Email value object (also used as the email sender ID)
 *
 * Add new rules here, register them in `validateCreateInstitution`,
 * and the form picks them up automatically.
 */

// ---------- Constants ----------

export const BANK_ONE_CODE_MAX_LENGTH = 7
export const BANK_ONE_CODE_PATTERN = /^\d{3,7}$/

export const ALIAS_MIN_LENGTH = 3
export const ALIAS_MAX_LENGTH = 50
// Letters, digits, hyphen, underscore. ASCII only: the alias is used as an
// address, so this is intentionally a touch stricter than the backend check.
export const ALIAS_PATTERN = /^[A-Za-z0-9_-]+$/

export const EMAIL_MAX_LENGTH = 254
// name@domain.tld: no spaces, a single @, at least one dot in the domain,
// and a TLD of 2+ letters.
export const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/

// ---------- Types ----------

export type RuleFields = 'bankOneCode' | 'alias' | 'adminEmail'
export type CreateInstitutionErrors = Partial<Record<RuleFields, string>>

/** Returns an error message, or undefined when the value is valid. */
export type Rule = (value: string) => string | undefined

// ---------- Sanitizers (applied as the user types) ----------

/** Strip non-digits and cap the length. */
export const sanitizeBankOneCode = (value: string): string =>
    value.replace(/\D/g, '').slice(0, BANK_ONE_CODE_MAX_LENGTH)

// ---------- Rules ----------

export const validateBankOneCode: Rule = (value) =>
    BANK_ONE_CODE_PATTERN.test(value) ? undefined : 'BankOne code must be 3-7 digits'

export const validateAlias: Rule = (value) => {
    const alias = value.trim() // the backend trims too

    if (!alias) return 'Alias is required'

    if (alias.length < ALIAS_MIN_LENGTH || alias.length > ALIAS_MAX_LENGTH) {
        return `Alias must be ${ALIAS_MIN_LENGTH}-${ALIAS_MAX_LENGTH} characters`
    }

    if (!ALIAS_PATTERN.test(alias)) {
        return 'Alias can only contain letters, numbers, hyphens (-) and underscores (_). No spaces or dots.'
    }

    return undefined
}

export const validateEmail: Rule = (value) => {
    const email = value.trim()

    if (!email) return 'Email address is required'

    if (email.length > EMAIL_MAX_LENGTH) {
        return `Email address must be ${EMAIL_MAX_LENGTH} characters or fewer`
    }

    if (!EMAIL_PATTERN.test(email)) {
        return 'Enter a valid email address, e.g. name@example.com'
    }

    return undefined
}

// ---------- Form-level validation ----------

export const validateCreateInstitution = (
    form: Pick<CreateInstitutionForm, RuleFields>,
): CreateInstitutionErrors => {
    const errors: CreateInstitutionErrors = {}

    const bankOneCodeError = validateBankOneCode(form.bankOneCode)
    if (bankOneCodeError) errors.bankOneCode = bankOneCodeError

    const aliasError = validateAlias(form.alias)
    if (aliasError) errors.alias = aliasError

    const adminEmailError = validateEmail(form.adminEmail)
    if (adminEmailError) errors.adminEmail = adminEmailError

    return errors
}